import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { BRANDS, COUNTRIES, COMMISSION_RATE, MAX_CUSTOM_AMOUNT } from '../mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { ShieldCheck, Zap, Mail, Bitcoin, ChevronRight, Minus, Plus, Star } from 'lucide-react';
import BrandImage from '../components/BrandImage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductDetail = () => {
  const { brandId } = useParams();
  const brand = BRANDS.find(b => b.id === brandId);
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { formatPrice, currency, isUsd, countryCode: currencyCountry, setCountry: setCurrencyCountry } = useCurrency();
  const { user } = useAuth();
  const { toast } = useToast();

  const [selected, setSelected] = useState(brand?.denominations[1] || brand?.denominations[0]);
  const [custom, setCustom] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [isFav, setIsFav] = useState(false);
  const [favBusy, setFavBusy] = useState(false);
  const initialCountry = brand?.countries?.includes(currencyCountry) ? currencyCountry : (brand?.countries?.[0] || 'US');
  const [country, setCountryLocal] = useState(initialCountry);

  // Track this brand as recently viewed AND check favorite status — only for logged-in users.
  useEffect(() => {
    if (!user || !brand) return;
    const logoUrl = brand.logoUrl || `https://cdn.simpleicons.org/${brand.slug}/${brand.color.replace('#','')}`;
    // Fire-and-forget: don't block the page render if the beacon fails.
    axios.post(`${API}/user/recent`, { slug: brand.slug, name: brand.name, logo_url: logoUrl }).catch(() => {});
    // Check if this brand is already favorited
    axios.get(`${API}/user/favorites`).then(({ data }) => {
      setIsFav(data.some(f => f.slug === brand.slug));
    }).catch(() => {});
  }, [user, brand?.slug]);  // eslint-disable-line react-hooks/exhaustive-deps

  const toggleFavorite = useCallback(async () => {
    if (!user) {
      toast({ title: 'Sign in to save favorites', description: 'Create an account to keep a list of brands you love.' });
      return;
    }
    if (favBusy || !brand) return;
    setFavBusy(true);
    const logoUrl = brand.logoUrl || `https://cdn.simpleicons.org/${brand.slug}/${brand.color.replace('#','')}`;
    try {
      if (isFav) {
        await axios.delete(`${API}/user/favorites/${brand.slug}`);
        setIsFav(false);
        toast({ title: `${brand.name} removed from favorites` });
      } else {
        await axios.post(`${API}/user/favorites`, { slug: brand.slug, name: brand.name, logo_url: logoUrl });
        setIsFav(true);
        toast({ title: `${brand.name} added to favorites` });
      }
    } catch {
      toast({ title: 'Could not update favorites', variant: 'destructive' });
    } finally {
      setFavBusy(false);
    }
  }, [user, brand, isFav, favBusy, toast]);

  // Selecting a region for the gift card ALSO switches the display currency
  // for the whole site so prices are shown in the visitor's local money.
  const setCountry = (code) => {
    setCountryLocal(code);
    setCurrencyCountry(code);
  };

  if (!brand) return <div className="max-w-7xl mx-auto p-8">Brand not found.</div>;

  const rawCustom = custom ? parseFloat(custom) || 0 : 0;
  const finalAmount = custom ? Math.min(rawCustom, MAX_CUSTOM_AMOUNT) : selected;
  const customExceeds = custom !== '' && rawCustom > MAX_CUSTOM_AMOUNT;
  const faceTotal = finalAmount * quantity;
  const serviceFee = +(faceTotal * COMMISSION_RATE).toFixed(2);
  const grandTotal = +(faceTotal + serviceFee).toFixed(2);

  const handleAdd = () => {
    if (finalAmount <= 0) {
      toast({ title: 'Please choose a valid amount' });
      return;
    }
    if (finalAmount > MAX_CUSTOM_AMOUNT) {
      toast({ title: `Maximum amount is $${MAX_CUSTOM_AMOUNT}` });
      return;
    }
    addItem({
      brandId: brand.id, brandName: brand.name, brandSlug: brand.slug, brandDomain: brand.domain,
      brandLogo: `https://cdn.simpleicons.org/${brand.slug}/${brand.color.replace('#','')}`, brandColor: brand.color,
      denomination: finalAmount, country, quantity,
    });
    toast({ title: 'Added to cart', description: `${brand.name} · $${finalAmount} × ${quantity}` });
  };

  const handleBuyNow = () => {
    handleAdd();
    setTimeout(() => navigate('/checkout'), 100);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <nav className="text-xs text-neutral-500 mb-6">
        <Link to="/" className="hover:text-neutral-900">Home</Link>
        <ChevronRight className="inline w-3 h-3 mx-1" />
        <Link to={`/browse/${brand.category}`} className="hover:text-neutral-900 capitalize">{brand.category}</Link>
        <ChevronRight className="inline w-3 h-3 mx-1" />
        <span className="text-neutral-900">{brand.name}</span>
      </nav>

      <div className="grid lg:grid-cols-5 gap-8">
        {/* Product visual */}
        <div className="lg:col-span-2">
          <Card className="overflow-hidden border-neutral-200">
            <div className="aspect-[16/10] flex items-center justify-center p-10 relative" style={{ background: `linear-gradient(135deg, ${brand.color}18, ${brand.color}08)` }}>
              <BrandImage brand={brand} className="max-h-24 max-w-[240px] object-contain" tileClassName="w-32 h-24 rounded-2xl text-3xl" />
            </div>
            <div className="p-5 border-t border-neutral-100">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="secondary" className="bg-violet-50 text-[#8247E5] hover:bg-violet-50">
                  <Zap className="w-3 h-3 mr-1" /> Instant delivery
                </Badge>
                <Badge variant="secondary" className="bg-neutral-100">
                  <ShieldCheck className="w-3 h-3 mr-1" /> Guaranteed valid
                </Badge>
                <Badge variant="secondary" className="bg-neutral-100 capitalize">{brand.category}</Badge>
              </div>
            </div>
          </Card>

          <div className="mt-6 bg-white rounded-xl border border-neutral-200 p-5">
            <h3 className="font-semibold text-sm mb-3">How to redeem</h3>
            <ol className="text-sm text-neutral-600 space-y-2 list-decimal list-inside">
              <li>Complete checkout with your preferred cryptocurrency.</li>
              <li>Receive your gift code via email within seconds.</li>
              <li>Visit {brand.name}'s official redemption page and enter the code.</li>
              <li>Enjoy your credit. Codes never expire (in most regions).</li>
            </ol>
          </div>
        </div>

        {/* Purchase panel */}
        <div className="lg:col-span-3">
          <div className="flex items-start justify-between gap-4">
            <h1 className="font-display text-3xl md:text-4xl font-bold">{brand.name} Gift Card</h1>
            <button
              onClick={toggleFavorite}
              disabled={favBusy}
              data-testid="favorite-toggle-btn"
              aria-label={isFav ? 'Remove from favorites' : 'Add to favorites'}
              title={user ? (isFav ? 'Remove from favorites' : 'Add to favorites') : 'Sign in to save favorites'}
              className={`shrink-0 w-11 h-11 rounded-full flex items-center justify-center border transition-all
                ${isFav ? 'bg-amber-50 border-amber-200 text-amber-500 hover:bg-amber-100' : 'bg-white border-neutral-200 text-neutral-400 hover:border-neutral-300 hover:text-neutral-700'}`}>
              <Star className={`w-5 h-5 ${isFav ? 'fill-amber-400' : ''}`} strokeWidth={2} />
            </button>
          </div>
          <p className="text-neutral-600 mt-2">{brand.description}</p>

          {/* Country */}
          <div className="mt-6">
            <div className="text-sm font-semibold mb-2">Country / Region</div>
            <div className="flex flex-wrap gap-2">
              {brand.countries.map(code => {
                const c = COUNTRIES.find(x => x.code === code);
                if (!c) return null;
                const active = country === code;
                return (
                  <button key={code} onClick={() => setCountry(code)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm border transition ${
                      active ? 'border-[#8247E5] bg-violet-50 text-neutral-900' : 'border-neutral-200 bg-white hover:border-neutral-400'
                    }`}>
                    <span>{c.flag}</span> {c.name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Denominations */}
          <div className="mt-6">
            <div className="text-sm font-semibold mb-2">Choose amount</div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {brand.denominations.map(d => {
                const active = !custom && selected === d;
                return (
                  <button key={d} onClick={() => { setSelected(d); setCustom(''); }}
                    className={`h-16 rounded-xl border-2 text-base font-semibold transition flex flex-col items-center justify-center ${
                      active ? 'border-[#8247E5] bg-violet-50 text-neutral-900' : 'border-neutral-200 bg-white hover:border-neutral-400'
                    }`}>
                    <div>{formatPrice(d)}</div>
                    {!isUsd && <div className="text-[10px] text-neutral-500 font-normal">${d} USD</div>}
                  </button>
                );
              })}
            </div>
            <div className="mt-3 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500">$</span>
              <Input
                type="number"
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                min={1}
                max={MAX_CUSTOM_AMOUNT}
                placeholder={`Or enter custom amount in USD (max $${MAX_CUSTOM_AMOUNT})`}
                className="pl-7 h-11"
                data-testid="custom-amount-input"
              />
            </div>
            {customExceeds && (
              <p className="mt-2 text-xs text-red-600" data-testid="custom-amount-error">
                Maximum amount is ${MAX_CUSTOM_AMOUNT}. We'll cap it at ${MAX_CUSTOM_AMOUNT} for this order.
              </p>
            )}
            {!isUsd && (
              <p className="mt-2 text-[11px] text-neutral-500">
                Prices shown in {currency.code}. Gift cards are issued in USD value. You'll pay the crypto equivalent at checkout.
              </p>
            )}
          </div>

          {/* Quantity */}
          <div className="mt-6 flex items-center gap-4">
            <div className="text-sm font-semibold">Quantity</div>
            <div className="flex items-center border border-neutral-200 rounded-lg">
              <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="px-3 h-10 hover:bg-neutral-50"><Minus className="w-4 h-4" /></button>
              <div className="w-10 text-center font-semibold">{quantity}</div>
              <button onClick={() => setQuantity(quantity + 1)} className="px-3 h-10 hover:bg-neutral-50"><Plus className="w-4 h-4" /></button>
            </div>
          </div>

          {/* Total & CTAs */}
          <div className="mt-8 bg-white border border-neutral-200 rounded-2xl p-6">
            <div className="space-y-2 mb-4">
              <div className="flex items-baseline justify-between text-sm">
                <span className="text-neutral-600">Gift card value ({quantity} × {formatPrice(finalAmount)})</span>
                <span className="font-semibold" data-testid="face-total">{formatPrice(faceTotal)}</span>
              </div>
              <div className="flex items-baseline justify-between text-sm">
                <span className="text-neutral-600 flex items-center gap-1">
                  Service fee (10%)
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 text-[#8247E5] font-semibold">CRYPTO</span>
                </span>
                <span className="font-semibold" data-testid="service-fee">{formatPrice(serviceFee)}</span>
              </div>
              <div className="flex items-baseline justify-between pt-2 border-t border-neutral-200">
                <span className="text-sm font-semibold">You pay</span>
                <div className="text-right">
                  <span className="font-display text-3xl font-bold text-[#8247E5]" data-testid="grand-total">{formatPrice(grandTotal)}</span>
                  {!isUsd && <div className="text-[11px] text-neutral-500 mt-0.5">≈ ${grandTotal.toFixed(2)} USD</div>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-neutral-500 mb-4">
              <Bitcoin className="w-3.5 h-3.5 text-[#8247E5]" /> ≈ {(grandTotal * 0.0000152).toFixed(6)} BTC · <Zap className="w-3.5 h-3.5" /> Lightning ready
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button data-testid="add-to-cart-btn" onClick={handleAdd} variant="outline" className="h-12 flex-1 border-neutral-300 font-semibold">Add to cart</Button>
              <Button data-testid="buy-now-btn" onClick={handleBuyNow} className="h-12 flex-1 bg-[#8247E5] hover:bg-[#6d33cc] text-white font-semibold">Buy now</Button>
            </div>
            <div className="mt-4 flex items-center gap-2 text-xs text-neutral-500">
              <Mail className="w-3.5 h-3.5" /> Delivered to your email · no account required
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
